import React, {useMemo} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import type {HotspotExplorerProps, HotspotExplorerSortMode} from "../types";
import {
    buildHotspotExplorerSearch,
    buildCountyPath,
    buildHotspotDetailPath,
    buildStatePath,
    getCachedHotspotExplorerStats,
    getCachedSortedHotspots,
    getHotspotExplorerHeroImage,
    getHotspotExplorerPage,
    getStateProvinceLabel,
} from "../helpers";
import {HotspotExplorerShell} from "../hotspot-explorer-shell";
import {usePageTitle} from "../../../helpers/usePageTitle";

export function CountyHotspotsView({
    hotspotExplorerIndex,
    selectedYear,
    selectedMonth,
    yearOptions,
    onChangePeriod
}: HotspotExplorerProps) {
    const resolvedHotspotExplorerIndex = hotspotExplorerIndex!;
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const {countryCode = '', stateProvince = '', county = ''} = useParams();
    const sortMode = (searchParams.get('sort') as HotspotExplorerSortMode) || 'visits';
    const currentPage = getHotspotExplorerPage(searchParams);
    const stateKey = `${countryCode}|${stateProvince}`;
    const countyKey = `${countryCode}|${stateProvince}|${county}`;
    const hotspots = useMemo(() => resolvedHotspotExplorerIndex.hotspotsByCountyKey.get(countyKey) ?? [], [countyKey, resolvedHotspotExplorerIndex]);
    const regionBounds = hotspots[0]?.regionData?.subnational2?.bounds ?? undefined;
    const countyLabel = hotspots[0]?.countyLabel || county;
    const stateLabel = hotspots[0]?.stateLabel || getStateProvinceLabel(stateProvince);
    const visibleHotspots = useMemo(
        () => getCachedSortedHotspots(hotspots, sortMode),
        [hotspots, sortMode]
    );
    const countyGroups = resolvedHotspotExplorerIndex.countyGroupsByStateKey.get(stateKey) ?? [];
    const countryLabel = hotspots[0]?.countryLabel || countryCode;
    const showYearsExploring = selectedYear === undefined || selectedYear === 'life';

    usePageTitle(countyLabel, stateLabel, countryLabel, 'Hotspot Explorer');

    const setSortMode = (nextSortMode: HotspotExplorerSortMode) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('sort', nextSortMode);
        setSearchParams(nextParams, {replace: true});
    }

    const setPage = (page: number) => {
        const nextSearch = buildHotspotExplorerSearch(searchParams, {page});
        navigate(`${location.pathname}${nextSearch}`, {replace: true});
    }

    const openCounty = (nextCounty: string) => {
        navigate(`${buildCountyPath(countryCode, stateProvince, nextCounty)}${buildHotspotExplorerSearch(searchParams, {page: 1})}`);
    }

    const openHotspot = (locationId: string) => {
        navigate(`${buildHotspotDetailPath(locationId)}${buildHotspotExplorerSearch(searchParams, {page: 1})}`, {
            state: {returnTo: `${location.pathname}${location.search}`}
        });
    }

    return (
        <HotspotExplorerShell
            backgroundImage={getHotspotExplorerHeroImage({routeLevel: 'county', regionBounds})}
            eyebrow="Hotspot Explorer"
            title={countyLabel}
            intro={`Hotspots you've visited in ${countyLabel}, ${stateLabel}.`}
            backLabel={`Back to ${stateLabel}`}
            onBack={() => navigate(`${buildStatePath(countryCode, stateProvince)}${buildHotspotExplorerSearch(searchParams, {page: 1})}`)}
            stats={getCachedHotspotExplorerStats(visibleHotspots, 'Counties')}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            yearOptions={yearOptions}
            onChangePeriod={onChangePeriod}
            showChildStat={false}
            showYearsExploring={showYearsExploring}
            panelHeading="By County"
            groups={countyGroups}
            activeGroupCode={county}
            onSelectGroup={openCounty}
            hotspots={visibleHotspots}
            sortMode={sortMode}
            onChangeSort={setSortMode}
            currentPage={currentPage}
            onPageChange={setPage}
            onOpenHotspot={openHotspot}
        />
    );
}
